import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash2, Upload, Pencil, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { Card, Field, TextInput } from "./kit";

export default function ClientCountriesManager() {
  const [countries, setCountries] = useState([]);
  const [name, setName] = useState("");
  const [count, setCount] = useState("");
  const [flagUrl, setFlagUrl] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", clients_served: 0 });
  const addFileRef = useRef(null);
  const rowFileRef = useRef(null);

  const load = () => api.get("/admin/client-countries").then(({ data }) => setCountries(data));
  useEffect(() => { load(); }, []);

  const upload = async (file, setter) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setter(data.url);
      toast.success("Flag uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  const add = async () => {
    if (!name.trim()) return toast.error("Country name is required");
    await api.post("/admin/client-countries", { name: name.trim(), flag_url: flagUrl, clients_served: parseInt(count || "0", 10) || 0 });
    setName("");
    setCount("");
    setFlagUrl("");
    toast.success("Country added — live on the hero now");
    load();
  };

  const startEdit = (c) => { setEditingId(c.id); setEditDraft({ name: c.name, clients_served: c.clients_served }); };
  const saveEdit = async () => {
    const orig = countries.find((c) => c.id === editingId);
    if (!orig) return;
    await api.put(`/admin/client-countries/${editingId}`, { name: editDraft.name.trim() || orig.name, flag_url: orig.flag_url, clients_served: parseInt(editDraft.clients_served || "0", 10) || 0 });
    setEditingId(null);
    toast.success("Country updated — live on the site now");
    load();
  };

  const remove = async (c) => {
    await api.delete(`/admin/client-countries/${c.id}`);
    toast.success("Country removed");
    load();
  };

  const move = async (c, dir) => {
    const idx = countries.findIndex((x) => x.id === c.id);
    const swap = idx + dir;
    if (swap < 0 || swap >= countries.length) return;
    const next = [...countries];
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setCountries(next);
    await api.post("/admin/client-countries/reorder", { ids: next.map((x) => x.id) });
    load();
  };

  return (
    <div className="space-y-6" data-testid="admin-countries-manager">
      <Card title="Client Countries" desc="Circular flags shown in the hero, below the trust badges. Hover visitors see the country name and clients served count. Upload a square flag image for best results.">
        <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end">
          <Field label="Country name" className="flex-1">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. United States" data-testid="countries-name-input" />
          </Field>
          <Field label="Clients served" className="w-36">
            <TextInput type="number" min="0" value={count} onChange={(e) => setCount(e.target.value)} placeholder="e.g. 48" data-testid="countries-count-input" />
          </Field>
          <div className="flex gap-3">
            <button className="btn btn-sm btn-outline-light" onClick={() => addFileRef.current?.click()} data-testid="countries-upload-button">
              <Upload size={14} /> Upload flag
            </button>
            <input ref={addFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], setFlagUrl)} />
            <button className="btn btn-sm btn-primary" onClick={add} data-testid="countries-add-button">Add Country</button>
          </div>
        </div>
        {flagUrl && (
          <div className="mt-4 flex items-center gap-3 text-xs text-cyan-300">
            <img src={flagUrl} alt="" className="h-8 w-8 rounded-full object-cover" />
            <span>Flag ready — it will be used for the next country you add</span>
          </div>
        )}
      </Card>

      <div className="bg-[#0E1642] border border-white/10 rounded-2xl p-6">
        <h3 className="font-display font-bold text-white mb-4">Current countries ({countries.length})</h3>
        <div className="space-y-3">
          {countries.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-[#0A0F2C]/40 p-3" data-testid={`countries-row-${c.id}`}>
              {editingId === c.id ? (
                <>
                  <button className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 border border-[rgba(0,207,255,0.4)]" onClick={() => rowFileRef.current?.click()} aria-label="Change flag">
                    <img src={c.flag_url} alt="" className="w-full h-full object-cover" />
                  </button>
                  <input ref={rowFileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0], async (url) => { await api.put(`/admin/client-countries/${c.id}`, { name: editDraft.name, flag_url: url, clients_served: parseInt(editDraft.clients_served || "0", 10) || 0 }); toast.success("Flag updated"); load(); })} />
                  <TextInput className="flex-1" value={editDraft.name} onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })} data-testid="countries-edit-name" />
                  <TextInput className="w-24" type="number" min="0" value={editDraft.clients_served} onChange={(e) => setEditDraft({ ...editDraft, clients_served: e.target.value })} data-testid="countries-edit-count" />
                  <button onClick={saveEdit} className="p-1.5 text-emerald-400 hover:text-emerald-300 transition-colors" aria-label="Save changes" data-testid={`countries-save-${c.id}`}><Check size={16} /></button>
                  <button onClick={() => setEditingId(null)} className="p-1.5 text-slate-500 hover:text-white transition-colors" aria-label="Cancel editing"><X size={16} /></button>
                </>
              ) : (
                <>
                  {c.flag_url ? (
                    <img src={c.flag_url} alt={c.name} className="w-9 h-9 rounded-full object-cover border border-[rgba(0,207,255,0.4)] flex-shrink-0" />
                  ) : (
                    <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{(c.name || "?")[0]}</span>
                  )}
                  <div className="flex-1 font-semibold text-white text-sm">{c.name}</div>
                  <div className="text-xs text-slate-400" data-testid={`countries-count-${c.id}`}>{c.clients_served} clients served</div>
                  <button onClick={() => startEdit(c)} className="p-1.5 text-slate-500 hover:text-cyan-300 transition-colors" aria-label="Edit country" data-testid={`countries-edit-${c.id}`}><Pencil size={15} /></button>
                  <button onClick={() => move(c, -1)} className="p-1.5 text-slate-500 hover:text-cyan-300 transition-colors" aria-label="Move up"><ArrowUp size={15} /></button>
                  <button onClick={() => move(c, 1)} className="p-1.5 text-slate-500 hover:text-cyan-300 transition-colors" aria-label="Move down"><ArrowDown size={15} /></button>
                  <button onClick={() => remove(c)} className="p-1.5 text-slate-500 hover:text-red-400 transition-colors" aria-label="Remove country" data-testid={`countries-delete-${c.id}`}><Trash2 size={15} /></button>
                </>
              )}
            </div>
          ))}
          {countries.length === 0 && <div className="py-6 text-center text-slate-500 text-sm">No countries yet — add the first one above.</div>}
        </div>
      </div>
    </div>
  );
}
